/**
 * Calculates the difference in days between a given date and today
 * @param dateString - Date string in ISO format (e.g., "2025-05-01T08:13:26Z")
 * @returns The difference in days as a whole number (positive if future, negative if past)
 */
export function getDaysDifferenceFromToday(dateString: string): number {
  try {
    const targetDate = new Date(dateString);
    const today = new Date();

    // Reset time part to midnight for accurate day calculation
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    // Calculate difference in milliseconds and convert to days
    const diffTime = today.getTime() - targetDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  } catch (error) {
    console.error("Error calculating date difference:", error);
    return 0;
  }
}

/**
 * Gets the date range for month-to-date (MTD)
 * @returns Object with startDate (first day of current month) and endDate (today)
 */
export function getMonthToDateRange(): { startDate: Date; endDate: Date } {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);

  return { startDate, endDate };
}

/**
 * Formats a Date object to YYYY-MM-DD string for HTML date input
 * @param date - Date object to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parses a date string from HTML date input to Date object
 * Sets time to start of day (00:00:00.000) for start dates
 * @param dateString - Date string in YYYY-MM-DD format or null
 * @param isEndDate - If true, sets time to end of day (23:59:59.999)
 * @returns Date object or undefined if invalid
 */
export function parseDateFromInput(
  dateString: string | null,
  isEndDate: boolean = false,
): Date | undefined {
  if (!dateString) return undefined;

  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  if (isNaN(date.getTime())) return undefined;

  if (isEndDate) {
    date.setHours(23, 59, 59, 999);
  } else {
    date.setHours(0, 0, 0, 0);
  }

  return date;
}
